import { format } from 'date-fns';
import type { Post } from '../types';

export function getMonthsToDisplay(posts: Post[], primaryMonth: string): string[] {
  if (!posts || posts.length === 0) {
    return [primaryMonth];
  }

  const monthsSet = new Set(posts.map(post => post.date.substring(0, 7)));

  const months = Array.from(monthsSet).sort();

  if (!months.includes(primaryMonth)) {
    months.push(primaryMonth);
    months.sort();
  }

  return months;
}

export function calculateMonthRangeText(displayMonths: string[]): string {
  if (displayMonths.length === 0) return '';
  if (displayMonths.length === 1) {
    const [year, monthNum] = displayMonths[0].split('-').map(Number);
    return format(new Date(year, monthNum - 1, 1), 'MMMM yyyy');
  }

  const firstMonth = displayMonths[0];
  const lastMonth = displayMonths[displayMonths.length - 1];
  const [firstYear, firstMonthNum] = firstMonth.split('-').map(Number);
  const [lastYear, lastMonthNum] = lastMonth.split('-').map(Number);

  const firstDate = new Date(firstYear, firstMonthNum - 1, 1);
  const lastDate = new Date(lastYear, lastMonthNum - 1, 1);

  if (firstYear === lastYear) {
    return `${format(firstDate, 'MMMM')} - ${format(lastDate, 'MMMM yyyy')}`;
  }

  return `${format(firstDate, 'MMMM yyyy')} - ${format(lastDate, 'MMMM yyyy')}`;
}

export function sortPostsByDateTime(posts: Post[]): Post[] {
  return [...posts].sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA.getTime() - dateB.getTime();
  });
}

export function groupPostsByDate(posts: Post[]): Record<string, Post[]> {
  const postsByDate: Record<string, Post[]> = {};
  posts.forEach(post => {
    if (!postsByDate[post.date]) {
      postsByDate[post.date] = [];
    }
    postsByDate[post.date].push(post);
  });
  return postsByDate;
}

export function getFilenameFromMonths(displayMonths: string[], primaryMonth: string): string {
  return displayMonths.length > 1
    ? `${displayMonths[0]}_to_${displayMonths[displayMonths.length - 1]}`
    : primaryMonth;
}

export function generatePostId(): string {
  return `post_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function ensurePostsHaveIds(posts: Post[]): Post[] {
  // First, assign IDs and default status if missing
  const postsWithIds = posts.map(post => {
    const updates: Partial<Post> = {};

    if (!post.id) {
      updates.id = generatePostId();
    }

    // Default status to 'draft' if not specified
    if (!post.status) {
      updates.status = 'draft';
    }

    return Object.keys(updates).length > 0 ? { ...post, ...updates } : post;
  });

  // Then, assign order based on date/time sort if not present
  const sorted = sortPostsByDateTime(postsWithIds);
  return sorted.map((post, index) => {
    if (post.order === undefined) {
      return { ...post, order: index };
    }
    return post;
  });
}
